import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, ArrowLeft, Bot, Globe2, Layers3, ShieldCheck, WalletCards } from 'lucide-react';
import { Link } from 'react-router-dom';

const ESTATETT_TITLE = 'estateTT | Caribbean Real Estate Technology Platform by ACSE Capital Inc.';
const ESTATETT_DESCRIPTION =
  "estateTT is ACSE Capital Inc.'s flagship Caribbean real estate technology platform for property search, listing management, professional visibility, international user access, role-based workflows, and embedded estateTT AI support.";

const EstateTTPlatform = () => {
  useEffect(() => {
    window.scrollTo(0, 0);

    const previousTitle = document.title;
    const previousDescription = document.querySelector('meta[name="description"]')?.getAttribute('content') ?? '';

    document.title = ESTATETT_TITLE;

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement('meta');
      descriptionTag.setAttribute('name', 'description');
      document.head.appendChild(descriptionTag);
    }
    descriptionTag.setAttribute('content', ESTATETT_DESCRIPTION);

    return () => {
      document.title = previousTitle;
      if (descriptionTag) {
        descriptionTag.setAttribute('content', previousDescription);
      }
    };
  }, []);

  const supportItems = [
    {
      title: 'Property search and listing activity',
      description:
        'Support for Caribbean property search, listing management, professional profiles, and property-linked visibility.',
    },
    {
      title: 'Role-based real estate workflows',
      description:
        'Workspace support for buyers, sellers, landlords, agents, brokers, developers, lenders, valuators, notaries, and service providers.',
    },
    {
      title: 'International user access',
      description:
        'Access for international buyers, investors, property owners, professionals, and businesses interested in Caribbean real estate markets.',
    },
    {
      title: 'Document and request organization',
      description:
        'A clearer way to organize documents, requests, messages, leads, follow-ups, and property-linked activity.',
    },
    {
      title: 'estateTT AI workflow support',
      description:
        'Embedded workflow intelligence that helps users organize next steps, identify missing information, and keep property activity clearer.',
    },
    {
      title: 'Professional handoff clarity',
      description:
        'Support for clearer coordination around property activity while keeping professional decisions with qualified professionals.',
    },
  ];

  return (
    <div className="pt-28 pb-20 bg-gradient-to-b from-blue-50 via-white to-gray-50">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/" className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700 mb-8">
            <ArrowLeft size={16} className="mr-2" />
            Back to ACSE Capital
          </Link>

          <div className="flex justify-center mb-6">
            <img
              src="/estatett-logo.webp"
              alt="estateTT logo"
              className="h-14 md:h-16 w-auto object-contain"
            />
          </div>

          <motion.section
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl overflow-hidden shadow-2xl border border-gray-100 bg-white mb-12"
          >
            <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr]">
              <div className="p-8 md:p-12">
                <div className="inline-flex items-center rounded-full bg-primary-50 px-3 py-1 text-sm font-medium text-primary-600 mb-6">
                  <Layers3 size={16} className="mr-2" />
                  Flagship Platform
                </div>
                <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-gray-900 mb-4">estateTT</h1>
                <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-6">
                  Caribbean real estate technology with embedded workflow intelligence.
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-8">
                  estateTT is ACSE Capital Inc.'s flagship software platform for Caribbean property activity. The platform is
                  being built to support property search, listing management, professional visibility, document coordination,
                  role-based workflows, international user access, and estateTT AI assistance across the property process.
                </p>
                <a
                  href="https://www.estatett.com"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors"
                >
                  Visit estateTT.com
                  <ExternalLink size={18} className="ml-2" />
                </a>
              </div>

              <div className="bg-gradient-to-br from-primary-600 via-primary-500 to-sky-500 p-8 md:p-12 text-white flex items-center">
                <p className="text-lg leading-relaxed text-primary-50">
                  estateTT brings Caribbean property activity into a clearer software environment for search, listing
                  management, role-based workflows, international access, and estateTT AI support.
                </p>
              </div>
            </div>
          </motion.section>

          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-8 mb-10">
            <motion.section
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-3xl border border-gray-100 shadow-lg p-8 md:p-10"
            >
              <div className="flex items-center mb-5 text-primary-600">
                <Globe2 size={22} className="mr-3" />
                <h3 className="text-2xl font-bold text-gray-900">
                  Built for Caribbean property activity, accessible internationally
                </h3>
              </div>
              <p className="text-gray-600 leading-relaxed mb-5">
                estateTT is focused on Caribbean real estate markets while remaining accessible to regional and international
                users. This includes U.S.-based buyers, investors, property owners, professionals, and businesses that need
                a clearer way to search, organize, and manage Caribbean property activity through a software platform.
              </p>
              <p className="text-gray-700 leading-relaxed font-medium">
                International users remain responsible for working with the appropriate qualified professionals in the
                relevant market when advice, representation, financing, valuation, title, tax, or regulatory guidance is
                required.
              </p>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="bg-white rounded-3xl border border-gray-100 shadow-lg p-8 md:p-10"
            >
              <div className="flex items-center mb-5 text-primary-600">
                <ShieldCheck size={22} className="mr-3" />
                <h3 className="text-2xl font-bold text-gray-900">Preparing for launch</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                estateTT is preparing for launch as ACSE Capital Inc.'s flagship Caribbean real estate technology platform.
              </p>
            </motion.section>
          </div>

          <motion.section
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-3xl border border-gray-100 shadow-lg p-8 md:p-10 mb-10"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">What estateTT supports</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {supportItems.map((item) => (
                <div key={item.title} className="rounded-2xl bg-gray-50 border border-gray-100 p-5 text-gray-700 leading-relaxed">
                  <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </motion.section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
            <motion.section
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="bg-white rounded-3xl border border-gray-100 shadow-lg p-8 md:p-10"
            >
              <div className="flex items-center mb-5 text-primary-600">
                <Bot size={22} className="mr-3" />
                <h3 className="text-2xl font-bold text-gray-900">estateTT AI</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                estateTT AI is being developed as an embedded intelligence layer inside the property workflow. It helps
                users organize next steps, identify missing information, keep follow-ups clearer, and understand where
                professional attention may be needed based on their role, workspace, property context, documents, requests,
                messages, and transaction activity.
              </p>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-3xl border border-gray-100 shadow-lg p-8 md:p-10"
            >
              <div className="flex items-center mb-5 text-primary-600">
                <WalletCards size={22} className="mr-3" />
                <h3 className="text-2xl font-bold text-gray-900">Software revenue model</h3>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                estateTT's planned revenue model includes SaaS subscription fees, platform access fees, listing-related
                platform fees, and other software-related charges paid to ACSE Capital Inc. for use of the platform.
              </p>
              <p className="text-gray-700 leading-relaxed font-medium">
                estateTT's fees are software and platform-related charges paid for use of the product. Property purchase
                funds, escrow arrangements, mortgage proceeds, seller proceeds, and professional commissions remain outside
                the estateTT platform.
              </p>
            </motion.section>
          </div>

          <motion.section
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="bg-slate-900 text-white rounded-3xl shadow-xl p-8 md:p-10"
          >
            <h3 className="text-2xl font-bold mb-5">Professional roles remain professional</h3>
            <p className="text-slate-200 leading-relaxed text-lg">
              estateTT supports organization, visibility, and workflow clarity around Caribbean property activity. Decisions
              requiring legal, financial, tax, valuation, lending, title, regulatory, construction, certification,
              conveyancing, brokerage, or underwriting judgment should be handled by qualified professionals.
            </p>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default EstateTTPlatform;
